import React, { useState } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { SettingsSection } from "component/shared/settings-page";
import { SettingsDialog } from "component/shared/settings-dialog";
import { User } from "model/user";
import { SUPPORT_EMAIL } from "../support-contact";

const useStyles = makeStyles()((theme) => ({
	cancelLink: {
		color: theme.palette.error.main,
		fontWeight: "bold",
		fontSize: 16,
		padding: 0,
		minWidth: 0,
		textTransform: "none",
		"&:hover": {
			backgroundColor: "transparent",
			textDecoration: "underline",
		},
	},
	cancelModalBlock: {
		backgroundColor: theme.palette.custom.tableHeaderSurface,
		padding: 16,
		borderRadius: 4,
	},
	cancelModalText: {
		whiteSpace: "pre-line",
		fontSize: 16,
		color: theme.palette.custom.textPrimary,
		marginTop: 8,
	},
	cancelModalCopy: {
		padding: 0,
		minWidth: 0,
		textTransform: "none",
		fontSize: 14,
	},
}));

export const cancellationMessage = (user: User): string => {
	return [
		"Hello,",
		"I would like to cancel my iHomefinder Account",
		`CID: ${user.clientId}`,
		`Email Address: ${user.companyEmail ?? ""}`,
		`Account Name: ${user.name ?? ""}`,
	].join("\n");
};

interface Props {
	user: User;
}

export const CancelAccount: React.FC<Props> = ({ user }) => {
	const { classes } = useStyles();
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const message = cancellationMessage(user);
	return (
		<>
			<SettingsSection
				title="Cancel Account"
				description="To begin the cancellation process, contact our support team or submit a cancellation request below."
			>
				<Mui.Button
					variant="text"
					className={classes.cancelLink}
					onClick={() => setCancelModalOpen(true)}
				>
					Send Cancellation Request
				</Mui.Button>
			</SettingsSection>
			<SettingsDialog
				open={cancelModalOpen}
				title="Send Cancellation Request"
				onClose={() => {
					setCancelModalOpen(false);
					setCopied(false);
				}}
			>
				<Mui.Typography>
					Email the following information to{" "}
					<Mui.Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Mui.Link>
				</Mui.Typography>
				<Mui.Box className={classes.cancelModalBlock}>
					<CopyToClipboard text={message} onCopy={() => setCopied(true)}>
						<Mui.Button
							variant="text"
							color="primary"
							className={classes.cancelModalCopy}
							startIcon={<Icons.Copy size={14} />}
						>
							Copy
						</Mui.Button>
					</CopyToClipboard>
					<Mui.Typography className={classes.cancelModalText}>{message}</Mui.Typography>
				</Mui.Box>
				<Mui.Snackbar
					open={copied}
					autoHideDuration={4000}
					message="Cancellation message copied to clipboard"
					onClose={() => setCopied(false)}
				/>
			</SettingsDialog>
		</>
	);
};
